// components/ConfirmationModal.js
import React, { useEffect, useRef } from "react";

function ConfirmationModal({
  open,
  onClose,
  onConfirm,
  formSections,
  sectionGroups,
  formData,
  title,
  exmptitle
}) {
  const overlayRef = useRef(null);
  const modalRef = useRef(null);
  const titleId = "confirm-modal-title";
  const descId = "confirm-modal-desc";

  const h = React.createElement;

  useEffect(() => {
    if (!open) return;
    
    const previouslyFocused = document.activeElement;
    const selectors =
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

    const trapFocus = (e) => {
      if (e.key !== "Tab") return;
      const focusables = modalRef.current
        ? Array.from(modalRef.current.querySelectorAll(selectors))
        : [];
      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    const onKeydown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose && onClose();
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusFirst = () => {
      const focusables = modalRef.current
        ? Array.from(modalRef.current.querySelectorAll(selectors))
        : [];
      if (focusables.length > 0) {
        focusables[0].focus();
      } else if (modalRef.current) {
        modalRef.current.focus();
      }
    };
    const t = setTimeout(focusFirst, 0);

    document.addEventListener("keydown", onKeydown);
    modalRef.current && modalRef.current.addEventListener("keydown", trapFocus);

    return () => {
      clearTimeout(t);
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeydown);
      modalRef.current &&
        modalRef.current.removeEventListener("keydown", trapFocus);
      if (previouslyFocused && previouslyFocused.focus) {
        previouslyFocused.focus();
      }
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) {
      onClose && onClose();
    }
  };

  const renderTaxRevenue = () => {
    const fields = formSections?.taxrevenue?.fields || [];
    if (!fields.length) return null;

    return h(
      "section",
      { className: "mb-6" },
      h(
        "h3",
        { className: "mb-3 text-lg font-semibold text-gray-800" },
        "Tax Revenue Information"
      ),
      h(
        "div",
        { className: "grid grid-cols-1 md:grid-cols-2 gap-4" },
        fields.map((field) =>
          h(
            "div",
            {
              key: field.fieldname,
              className:
                "rounded-lg border border-gray-200 bg-gray-50 px-4 py-3",
            },
            h(
              "p",
              { className: "text-xs font-semibold text-gray-500" },
              field.displayname
            ),
            h(
              "p",
              { className: "mt-1 text-sm text-gray-900 break-words" },
              formData?.[field.fieldname] || "N/A"
            )
          )
        )
      )
    );
  };

  const renderSplitPayment = (group) => {
    const fields = group?.splitpayment?.fields || [];
    if (!fields.length) return null;

    return h(
      "div",
      { className: "mb-6" },
      h(
        "h4",
        { className: "mb-2 text-sm font-semibold text-gray-700" },
        `${title || "Split Payment"} Information`
      ),
      h(
        "div",
        { className: "grid grid-cols-1 md:grid-cols-2 gap-4" },
        fields.map((field) =>
          h(
            "div",
            {
              key: `${field.fieldname}_${group.id}`,
              className: "rounded-lg border border-gray-200 bg-white px-4 py-3",
            },
            h(
              "p",
              { className: "text-xs font-semibold text-gray-500" },
              field.displayname
            ),
            h(
              "p",
              { className: "mt-1 text-sm text-gray-900 break-words" },
              formData?.[`${field.fieldname}_${group.id}`] || "N/A"
            )
          )
        )
      )
    );
  };

  const renderExemptions = (group) => {
    const groups = Array.isArray(group?.exemptionGroups)
      ? group.exemptionGroups
      : [];
    const fields = group?.exemption?.fields || [];
    if (!groups.length || !fields.length) return null;

    return h(
      "div",
      null,
      h(
        "h4",
        { className: "mb-2 text-sm font-semibold text-gray-700" },
        `${exmptitle || "Exemption"} Information`
      ),
      groups.map((exemptionId) =>
        h(
          "div",
          {
            key: `exemption_${group.id}_${exemptionId}`,
            className: "mb-4 rounded-xl border border-blue-100 bg-blue-50 px-4 py-4",
          },
          h(
            "h5",
            { className: "mb-3 text-sm font-bold text-blue-900" },
            `Exemption ${exemptionId}`
          ),
          h(
            "div",
            { className: "grid grid-cols-1 md:grid-cols-2 gap-4" },
            fields.map((field) =>
              h(
                "div",
                {
                  key: `${field.fieldname}_${group.id}_${exemptionId}`,
                  className:
                    "rounded-lg border border-blue-100 bg-white px-4 py-3",
                },
                h(
                  "p",
                  { className: "text-xs font-semibold text-gray-500" },
                  field.displayname
                ),
                h(
                  "p",
                  { className: "mt-1 text-sm text-gray-900 break-words" },
                  formData?.[
                    `${field.fieldname}_${group.id}_${exemptionId}`
                  ] || "N/A"
                )
              )
            )
          )
        )
      )
    );
  };

  const headerIcon = h(
    "div",
    {
      className:
        "flex h-10 w-10 items-center justify-center rounded-full bg-[#711515]/10 text-[#711515]",
    },
    h(
      "svg",
      { className: "h-5 w-5", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor" },
      h("path", {
        strokeLinecap: "round",
        strokeLinejoin: "round",
        strokeWidth: "2",
        d: "M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z",
      })
    )
  );

  const headerLeft = h(
    "div",
    { className: "flex items-center gap-3" },
    headerIcon,
    h(
      "div",
      null,
      h(
        "h2",
        { id: titleId, className: "text-xl font-bold text-gray-900" },
        "Confirm Submission"
      ),
      h(
        "p",
        { className: "text-sm text-gray-600" },
        "Review the details below, then submit or go back to edit."
      )
    )
  );

  const closeBtn = h(
    "button",
    {
      type: "button",
      onClick: onClose,
      className:
        "inline-flex items-center justify-center rounded-md border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-[#711515]",
      "aria-label": "Close dialog",
    },
    h(
      "svg",
      { className: "h-5 w-5", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor" },
      h("path", { strokeLinecap: "round", strokeWidth: "2", d: "M6 18L18 6M6 6l12 12" })
    )
  );

  const header = h(
    "div",
    { className: "sticky top-0 z-10 flex items-center justify-between border-b bg-white px-6 py-4" },
    headerLeft,
    closeBtn
  );

  const content = h(
    "div",
    { id: descId, className: "px-6 py-5 overflow-y-auto max-h-[65vh]" },
    renderTaxRevenue(),
    Array.isArray(sectionGroups)
      ? sectionGroups.map((group) =>
          h(
            "section",
            { key: group.id, className: "mb-8" },
            h(
              "div",
              { className: "mb-3 flex items-center justify-between" },
              h("h3", { className: "text-lg font-semibold text-gray-800" }, `Line ${group.id}`)
            ),
            renderSplitPayment(group),
            renderExemptions(group)
          )
        )
      : null
  );

  const footer = h(
    "div",
    { className: "sticky bottom-0 z-10 flex items-center justify-end gap-3 border-t bg-white px-6 py-4" },
    h(
      "button",
      {
        type: "button",
        onClick: onClose,
        className:
          "inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-[#711515]",
      },
      "Back"
    ),
    h(
      "button",
      {
        type: "button",
        onClick: onConfirm,
        className:
          "inline-flex items-center justify-center rounded-md bg-[#711515] px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#5a1010] focus-visible:ring-2 focus-visible:ring-[#711515]",
      },
      "Submit"
    )
  );

  const dialog = h(
    "div",
    {
      ref: modalRef,
      role: "dialog",
      "aria-modal": "true",
      "aria-labelledby": titleId,
      "aria-describedby": descId,
      className:
        "relative w-[95%] md:w-[900px] max-h-[85vh] overflow-hidden rounded-2xl bg-white shadow-2xl border border-gray-200 focus:outline-none",
      tabIndex: -1,
    },
    header,
    content,
    footer
  );

  return h(
    "div",
    {
      ref: overlayRef,
      onClick: handleOverlayClick,
      className: "fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm transition-opacity",
      "aria-hidden": "false",
    },
    dialog
  );
}

export default ConfirmationModal;
